/**
 * PresentModeContext Stub
 *
 * Provides presentation mode functionality for dashboards.
 * Allows toggling fullscreen presentation view.
 */
import { createContext, useContext, useState, type ReactNode } from 'react';

interface PresentModeContextType {
  isPresenting: boolean;
  togglePresenting: () => void;
  startPresenting: () => void;
  stopPresenting: () => void;
}

const PresentModeContext = createContext<PresentModeContextType | undefined>(undefined);

export const usePresentMode = (): PresentModeContextType => {
  const context = useContext(PresentModeContext);
  if (!context) {
    // Return a no-op version when not inside provider
    return {
      isPresenting: false,
      togglePresenting: () => {},
      startPresenting: () => {},
      stopPresenting: () => {},
    };
  }
  return context;
};

interface PresentModeProviderProps {
  children: ReactNode;
}

export const PresentModeProvider = ({ children }: PresentModeProviderProps) => {
  const [isPresenting, setIsPresenting] = useState(false);

  const togglePresenting = () => setIsPresenting((prev) => !prev);
  const startPresenting = () => setIsPresenting(true);
  const stopPresenting = () => setIsPresenting(false);

  return (
    <PresentModeContext.Provider
      value={{ isPresenting, togglePresenting, startPresenting, stopPresenting }}
    >
      {children}
    </PresentModeContext.Provider>
  );
};
